import React, { memo, useEffect, useState, useCallback } from "react";
import { Input } from "components/Inputs";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";

interface Props {
	precinctId?: string;
	propertyId: string;
	defaultState?: any;
	onChange?: (buildingId: string, precinctId?: string) => void;
}

const constraints = {
	name: {
		presence: {
			message: "^ is required"
		}
	}
};

const AsyncSelect = ({ precinctId, propertyId, onChange, defaultState: defaultProps }: Props) => {
	const [options, setOptions] = useState([] as any[]);
	const [defaultState, setDefaultState] = useState(defaultProps);
	const [datasource] = useState(
		new Datasource({
			mainModelName: "building",
			perPage: 1000,
			currentPage: 1,
			sortBy: "name"
		})
	);

	const onSelectOption = useCallback(
		(buildingId: string) => {
			if (!buildingId) {
				// Building was cleared
				if (!_.isNil(onChange)) onChange("");
				setDefaultState({ ...defaultState, building_id: "" });
				return;
			}

			if (!_.isNil(onChange)) {
				// Find the selected building from options to get precinct info
				const selectedBuilding = _.find(options, { value: buildingId });
				if (selectedBuilding && selectedBuilding.item) {
					const precinctIdFromBuilding = selectedBuilding.item.precinct_id || (selectedBuilding.item.precinct && selectedBuilding.item.precinct.id);
					// Pass both buildingId and precinctId if available
					onChange(buildingId, precinctIdFromBuilding);
				} else {
					// Fallback: just pass buildingId if building not found in options
					onChange(buildingId);
				}
			}
			setDefaultState({ ...defaultState, building_id: buildingId });
		},
		[onChange, defaultState, options]
	);

	// Sync defaultState when prop changes (especially when building_id changes externally)
	useEffect(() => {
		if (defaultProps) {
			const newBuildingId = defaultProps.building_id;
			setDefaultState((prev: any) => {
				// Always update if building_id is explicitly provided and different
				// This ensures programmatic updates from parent component take precedence
				if (newBuildingId !== undefined && prev?.building_id !== newBuildingId) {
					// Always create a new object to ensure Input component detects the change
					return { ...defaultProps };
				}
				// Update other properties (like errors) even if building_id hasn't changed
				return { ...defaultProps };
			});
		}
	}, [defaultProps]);

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			const options = _.map(response.normalizedMainModelResponse, (building: any) => {
				return {
					item: building,
					value: building.id,
					label: _.compact([building.name, building.precinct.name]).join(" ")
				};
			});
			setOptions([{
				item: {
					id: "",
					name: ""
				},
				value: "",
				label: "Select a building"
			}, ...options]);
		});
		return () => sub.unsubscribe();
	}, [datasource]);

	// API call
	useEffect(() => {
		if (_.isEmpty(propertyId)) return;
		const params: any = {
			property_id: propertyId,
			sort: datasource.sortBy,
			per_page: datasource.perPage,
			page: datasource.currentPage
		};
		
		if (precinctId) {
			params.precinct_id = precinctId;
		}
		
		datasource.get("v2", "buildings", {
			queryStringParameters: params
		});
	}, [datasource, propertyId, precinctId, datasource.currentPage, datasource.perPage, datasource.sortBy]);

	if (_.isEmpty(propertyId) || options.length === 0) return null;

	return (
		<Input
			onChange={onSelectOption}
			name="building_id"
			defaultState={defaultState}
			required={constraints}
			options={options}
			showIf={true}
		/>
	);
};

export default memo(AsyncSelect);
